import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import api, { downloadTemplate } from "../api";
import { Modal, useTags, TagChips, TagPicker, KindBadge, KINDS } from "../components/ui";
import QuickActions from "../components/QuickActions";
import usePersistedState from "../hooks/usePersistedState";

const EMPTY = { first_name: "", last_name: "", kind: "cliente", email: "", phone: "", role: "", fiscal_code: "", notes: "", tag_ids: [] };

export default function Persons({ embedded }) {
  const { tags } = useTags();
  const [list, setList] = useState([]);
  const [q, setQ] = usePersistedState("persons.q", "");
  const [tagFilter, setTagFilter] = usePersistedState("persons.tag", "");
  const [kindFilter, setKindFilter] = usePersistedState("persons.kind", "");
  const [modal, setModal] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [linkDraft, setLinkDraft] = useState({ company_id: "", role: "" });
  useEffect(() => { api.get("/companies").then((r) => setCompanies(r.data)).catch(() => {}); }, []);

  const load = useCallback(() => {
    const params = new URLSearchParams();
    if (q) params.set("q", q);
    if (tagFilter) params.set("tag_id", tagFilter);
    if (kindFilter) params.set("kind", kindFilter);
    api.get(`/persons?${params.toString()}`).then((r) => setList(r.data)).catch(() => {});
  }, [q, tagFilter, kindFilter]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    const { data, editing } = modal;
    const payload = { ...data };
    Object.keys(payload).forEach((k) => { if (payload[k] === "") delete payload[k]; });
    payload.tag_ids = data.tag_ids;
    try {
      if (editing) await api.put(`/persons/${data.id}`, payload);
      else {
        const r = await api.post("/persons", payload);
        for (const l of modal.links || []) {
          try { await api.post("/persons/links", { person_id: r.data.id, company_id: l.company_id, role: l.role || null }); }
          catch (err) { console.error("Errore collegamento azienda:", err); }
        }
      }
      setModal(null); load();
    } catch (e) { alert("Errore: " + (e.response?.data?.detail || e.message)); }
  };

  const addModalLink = () => {
    if (!linkDraft.company_id) return;
    const c = companies.find((x) => x.id === linkDraft.company_id);
    setModal((m) => ({ ...m, links: [...(m.links || []).filter((l) => l.company_id !== linkDraft.company_id), { company_id: linkDraft.company_id, role: linkDraft.role, name: c?.name || "" }] }));
    setLinkDraft({ company_id: "", role: "" });
  };
  const removeModalLink = (cid) => setModal((m) => ({ ...m, links: (m.links || []).filter((l) => l.company_id !== cid) }));

  const remove = async (id) => {
    if (!window.confirm("Eliminare la persona e i suoi collegamenti?")) return;
    await api.delete(`/persons/${id}`); load();
  };

  const f = (k, v) => setModal((m) => ({ ...m, data: { ...m.data, [k]: v } }));

  return (
    <div>
      {!embedded && <h1 className="page-title">Persone</h1>}
      {!embedded && <p className="page-sub">Referenti, consulenti e altri contatti — collegabili a più aziende</p>}

      <div className="toolbar">
        <input style={{ maxWidth: 260 }} placeholder="Cerca per nome, cognome, email…" value={q} onChange={(e) => setQ(e.target.value)} />
        <select style={{ maxWidth: 160 }} value={kindFilter} onChange={(e) => setKindFilter(e.target.value)} data-testid="person-kind-filter">
          <option value="">Tutti i tipi</option>
          {KINDS.map((k) => <option key={k.value} value={k.value}>{k.label}</option>)}
        </select>
        <select style={{ maxWidth: 180 }} value={tagFilter} onChange={(e) => setTagFilter(e.target.value)}>
          <option value="">Tutte le etichette</option>
          {tags.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
        </select>
        {(q || kindFilter || tagFilter) && (
          <button className="btn sm" onClick={() => { setQ(""); setKindFilter(""); setTagFilter(""); }} data-testid="persons-clear-filters">Azzera filtri</button>
        )}
        <div className="spacer" />
        <Link className="btn" to="/import">⬆ Importa da Excel</Link>
        <button className="btn" onClick={() => downloadTemplate("persons").catch((e) => alert("Errore: " + (e.response?.data?.detail || e.message)))}>⬇ Template Excel</button>
        <button className="btn primary" onClick={() => { setModal({ data: { ...EMPTY }, links: [], editing: false }); setLinkDraft({ company_id: "", role: "" }); }} data-testid="new-person-btn">+ Nuova persona</button>
      </div>

      <table>
        <thead><tr><th>Cognome e nome</th><th>Tipo</th><th>Ruolo</th><th>Email</th><th>Telefono</th><th>Etichette</th><th></th></tr></thead>
        <tbody>
          {list.map((p) => (
            <tr key={p.id}>
              <td><Link to={`/persone/${p.id}`}>{p.last_name} {p.first_name}</Link></td>
              <td><KindBadge kind={p.kind} /></td>
              <td>{p.role || "—"}</td>
              <td>{p.email || "—"}</td>
              <td>{p.phone || "—"}</td>
              <td><TagChips tagIds={p.tag_ids} tags={tags} /></td>
              <td style={{ whiteSpace: "nowrap" }}>
                <button className="btn sm" onClick={() => setModal({ data: { ...EMPTY, ...p }, editing: true })}>Modifica</button>{" "}
                <QuickActions target={{ kind: "person", id: p.id, name: `${p.first_name || ""} ${p.last_name || ""}`.trim(), email: p.email, phone: p.phone, company: p.company }} />{" "}
                <button className="btn sm danger" onClick={() => remove(p.id)}>Elimina</button>
              </td>
            </tr>
          ))}
          {list.length === 0 && <tr><td colSpan={7}><div className="empty">Nessuna persona.</div></td></tr>}
        </tbody>
      </table>

      {modal && (
        <Modal title={modal.editing ? "Modifica persona" : "Nuova persona"} onClose={() => setModal(null)}>
          <div className="grid-2">
            <div className="field"><label>Nome *</label><input value={modal.data.first_name} onChange={(e) => f("first_name", e.target.value)} /></div>
            <div className="field"><label>Cognome *</label><input value={modal.data.last_name} onChange={(e) => f("last_name", e.target.value)} /></div>
          </div>
          <div className="grid-2">
            <div className="field"><label>Tipo</label>
              <select value={modal.data.kind} onChange={(e) => f("kind", e.target.value)}>
                {KINDS.map((k) => <option key={k.value} value={k.value}>{k.label}</option>)}
              </select>
            </div>
            <div className="field"><label>Ruolo / qualifica</label><input placeholder="es. Consulente paghe" value={modal.data.role} onChange={(e) => f("role", e.target.value)} /></div>
          </div>
          <div className="grid-2">
            <div className="field"><label>Email</label><input value={modal.data.email} onChange={(e) => f("email", e.target.value)} /></div>
            <div className="field"><label>Telefono</label><input value={modal.data.phone} onChange={(e) => f("phone", e.target.value)} /></div>
          </div>
          <div className="field"><label>Codice fiscale</label><input value={modal.data.fiscal_code} onChange={(e) => f("fiscal_code", e.target.value)} /></div>
          <div className="field"><label>Note</label><textarea rows={2} value={modal.data.notes} onChange={(e) => f("notes", e.target.value)} /></div>
          <div className="field"><label>Etichette</label><TagPicker tags={tags} selected={modal.data.tag_ids} onChange={(v) => f("tag_ids", v)} /></div>
          {!modal.editing ? (
            <div className="field" data-testid="person-links-section">
              <label>Aziende collegate <span className="muted">(es. il consulente paghe può essere collegato a più aziende)</span></label>
              {(modal.links || []).length > 0 && (
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 6 }}>
                  {modal.links.map((l) => (
                    <span key={l.company_id} className="chip" data-testid={`person-link-chip-${l.company_id}`}>
                      {l.name}{l.role ? ` · ${l.role}` : ""}{" "}
                      <button onClick={() => removeModalLink(l.company_id)} style={{ background: "none", border: "none", cursor: "pointer", color: "inherit", fontWeight: 700 }} title="Rimuovi">✕</button>
                    </span>
                  ))}
                </div>
              )}
              <div className="toolbar" style={{ gap: 6 }}>
                <select style={{ maxWidth: 220 }} value={linkDraft.company_id} onChange={(e) => setLinkDraft((d) => ({ ...d, company_id: e.target.value }))} data-testid="person-link-company">
                  <option value="">— scegli azienda —</option>
                  {companies.filter((c) => !(modal.links || []).some((l) => l.company_id === c.id)).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
                <input style={{ maxWidth: 180 }} placeholder="Ruolo (es. Consulente paghe)" value={linkDraft.role} onChange={(e) => setLinkDraft((d) => ({ ...d, role: e.target.value }))} data-testid="person-link-role" />
                <button className="btn sm" onClick={addModalLink} disabled={!linkDraft.company_id} data-testid="person-link-add">＋ Aggiungi</button>
              </div>
            </div>
          ) : (
            <p className="muted" style={{ fontSize: 12 }}>Le aziende collegate si gestiscono dalla scheda della persona (pannello "Aziende collegate").</p>
          )}
          <div className="modal-actions">
            <button className="btn" onClick={() => setModal(null)}>Annulla</button>
            <button className="btn primary" onClick={save} disabled={!modal.data.first_name || !modal.data.last_name}>Salva</button>
          </div>
        </Modal>
      )}
    </div>
  );
}
